// Mock GraphQL client for local testing
export class MockGraphQLClient {
  constructor() {
    this.mockData = {
      articles: [
        {
          id: "1",
          title: "AWS Announces New Serverless Features",
          description: "Amazon Web Services has announced several new serverless computing features that will help developers build more efficient applications. These updates include improved cold start performance, enhanced monitoring capabilities, and better integration with other AWS services.",
          link: "https://aws.amazon.com/blogs/aws/new-serverless-features",
          published: "2025-06-27T06:00:00Z",
          author: "AWS Team",
          site_name: "AWS Blog",
          tags: ["AWS", "Serverless", "Lambda", "Cloud Computing"],
          content_snippet: "New serverless features announced..."
        },
        {
          id: "2", 
          title: "The Future of GraphQL in Modern Web Development",
          description: "GraphQL continues to revolutionize how we think about API design and data fetching. This comprehensive guide explores the latest trends, best practices, and real-world implementations of GraphQL in production applications.",
          link: "https://techcrunch.com/graphql-future",
          published: "2025-06-27T04:30:00Z",
          author: "Jane Developer",
          site_name: "TechCrunch",
          tags: ["GraphQL", "API", "Web Development", "Technology"],
          content_snippet: "GraphQL revolutionizes API design..."
        },
        {
          id: "3",
          title: "React 19 Beta: What's New and Exciting",
          description: "The React team has released React 19 beta with groundbreaking features including automatic batching improvements, concurrent features, and enhanced developer experience. Learn about the new hooks and performance optimizations.",
          link: "https://react.dev/blog/react-19-beta",
          published: "2025-06-27T02:15:00Z",
          author: "React Team",
          site_name: "React Blog",
          tags: ["React", "JavaScript", "Frontend", "Web Development"],
          content_snippet: "React 19 beta introduces new features..."
        },
        {
          id: "4",
          title: "Hacker News: Show HN - My RSS Aggregator with GraphQL",
          description: "I built a serverless RSS aggregator using AWS CDK, React, and GraphQL. The application processes RSS feeds every 15 minutes and provides a modern interface for browsing technology news. Built with cost optimization in mind - runs for under $3/month!",
          link: "https://news.ycombinator.com/item?id=123456",
          published: "2025-06-27T01:00:00Z",
          author: "techbuilder",
          site_name: "Hacker News",
          tags: ["Show HN", "RSS", "GraphQL", "Serverless", "AWS"],
          content_snippet: "Show HN: RSS aggregator with GraphQL..."
        },
        {
          id: "5",
          title: "The Register: Cloud Costs Spiral Out of Control",
          description: "Enterprise cloud spending continues to grow at an alarming rate, with many organizations struggling to optimize their AWS, Azure, and GCP bills. Industry experts share strategies for cost management and resource optimization.",
          link: "https://www.theregister.com/cloud-costs-spiral",
          published: "2025-06-26T23:45:00Z",
          author: "Cloud Reporter",
          site_name: "The Register",
          tags: ["Cloud Computing", "Cost Optimization", "Enterprise", "AWS"],
          content_snippet: "Cloud costs continue to grow..."
        },
        {
          id: "6",
          title: "Ars Technica: AI and Machine Learning Trends 2025",
          description: "Artificial intelligence and machine learning continue to evolve rapidly. This in-depth analysis covers the latest developments in large language models, computer vision, and AI infrastructure that are shaping the technology landscape.",
          link: "https://arstechnica.com/ai-ml-trends-2025",
          published: "2025-06-26T20:30:00Z",
          author: "AI Researcher",
          site_name: "Ars Technica",
          tags: ["AI", "Machine Learning", "Technology", "Innovation"],
          content_snippet: "AI and ML trends for 2025..."
        }
      ],
      tags: ["AWS", "Serverless", "Lambda", "Cloud Computing", "GraphQL", "API", "Web Development", "Technology", "React", "JavaScript", "Frontend", "Show HN", "RSS", "Cost Optimization", "Enterprise", "AI", "Machine Learning", "Innovation"],
      feeds: [
        { url: "https://aws.amazon.com/blogs/aws/feed/", site_name: "AWS Blog", last_updated: "2025-06-27T06:00:00Z", article_count: 1, most_recent_article_date: "2025-06-27T06:00:00Z", status: "active" },
        { url: "https://techcrunch.com/feed/", site_name: "TechCrunch", last_updated: "2025-06-27T04:30:00Z", article_count: 1, most_recent_article_date: "2025-06-27T04:30:00Z", status: "active" },
        { url: "https://hnrss.org/frontpage", site_name: "Hacker News", last_updated: "2025-06-27T01:00:00Z", article_count: 1, most_recent_article_date: "2025-06-27T01:00:00Z", status: "active" },
        { url: "https://www.theregister.com/headlines.atom", site_name: "The Register", last_updated: "2025-06-26T23:45:00Z", article_count: 1, most_recent_article_date: "2025-06-26T23:45:00Z", status: "active" },
        { url: "https://feeds.arstechnica.com/arstechnica/index", site_name: "Ars Technica", last_updated: "2025-06-26T20:30:00Z", article_count: 1, most_recent_article_date: "2025-06-26T20:30:00Z", status: "active" }
      ]
    };
  }

  async query(query, variables = {}) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 200));

    // Parse the query to determine what to return
    if (query.includes('getArticles')) {
      return this.handleGetArticles(variables);
    } else if (query.includes('searchArticles')) {
      return this.handleSearchArticles(variables);
    } else if (query.includes('getTags')) {
      return { getTags: this.mockData.tags };
    } else if (query.includes('getFeeds')) {
      return { getFeeds: this.mockData.feeds };
    } else if (query.includes('updateFeed')) {
      return this.handleUpdateFeed(variables);
    } else if (query.includes('getArticlesByDateRange')) {
      return this.handleGetArticlesByDateRange(variables);
    }

    throw new Error(`Unknown query: ${query}`);
  }

  handleGetArticles(variables) {
    let articles = [...this.mockData.articles];
    
    // Apply filters
    if (variables.site_name) {
      articles = articles.filter(a => a.site_name === variables.site_name);
    }
    
    if (variables.tag) {
      articles = articles.filter(a => a.tags.includes(variables.tag));
    }
    
    if (variables.search) {
      const searchLower = variables.search.toLowerCase();
      articles = articles.filter(a => 
        a.title.toLowerCase().includes(searchLower) ||
        a.description.toLowerCase().includes(searchLower) ||
        a.site_name.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting
    if (variables.sort_by === 'oldest') {
      articles.sort((a, b) => new Date(a.published) - new Date(b.published));
    } else if (variables.sort_by === 'title') {
      articles.sort((a, b) => a.title.localeCompare(b.title));
    } else {
      // newest (default)
      articles.sort((a, b) => new Date(b.published) - new Date(a.published));
    }
    
    // Apply pagination
    const limit = variables.limit || 50;
    const startIndex = variables.nextToken ? parseInt(variables.nextToken) : 0;
    const endIndex = startIndex + limit;
    const paginatedArticles = articles.slice(startIndex, endIndex);
    
    return {
      getArticles: {
        items: paginatedArticles,
        total: articles.length,
        nextToken: endIndex < articles.length ? endIndex.toString() : null
      }
    };
  }

  handleSearchArticles(variables) {
    const query = variables.query.toLowerCase();
    let articles = [...this.mockData.articles];
    
    // Score articles based on search relevance
    const scoredArticles = articles.map(article => {
      let score = 0;
      
      if (article.title.toLowerCase().includes(query)) score += 10;
      if (article.description.toLowerCase().includes(query)) score += 5;
      if (article.site_name.toLowerCase().includes(query)) score += 3;
      if (article.tags.some(tag => tag.toLowerCase().includes(query))) score += 2;
      
      return { ...article, _score: score };
    }).filter(article => article._score > 0);
    
    // Sort by relevance
    scoredArticles.sort((a, b) => b._score - a._score);
    
    // Remove score from response
    const results = scoredArticles.map(({ _score, ...article }) => article);
    
    // Apply pagination
    const limit = variables.limit || 50;
    const startIndex = variables.nextToken ? parseInt(variables.nextToken) : 0;
    const endIndex = startIndex + limit;
    const paginatedResults = results.slice(startIndex, endIndex);
    
    return {
      searchArticles: {
        items: paginatedResults,
        total: results.length,
        nextToken: endIndex < results.length ? endIndex.toString() : null
      }
    };
  }

  handleGetArticlesByDateRange(variables) {
    const startDate = new Date(variables.start_date);
    const endDate = new Date(variables.end_date);
    
    const filteredArticles = this.mockData.articles.filter(article => {
      const articleDate = new Date(article.published);
      return articleDate >= startDate && articleDate <= endDate;
    });
    
    // Sort by newest first
    filteredArticles.sort((a, b) => new Date(b.published) - new Date(a.published));
    
    // Apply pagination
    const limit = variables.limit || 50;
    const startIndex = variables.nextToken ? parseInt(variables.nextToken) : 0;
    const endIndex = startIndex + limit;
    const paginatedArticles = filteredArticles.slice(startIndex, endIndex);
    
    return {
      getArticlesByDateRange: {
        items: paginatedArticles,
        total: filteredArticles.length,
        nextToken: endIndex < filteredArticles.length ? endIndex.toString() : null
      }
    };
  }

  handleUpdateFeed(variables) {
    // Simulate updating a feed in the mock data
    const { old_url, new_url, new_site_name } = variables;
    
    // Find and update the feed
    const feedIndex = this.mockData.feeds.findIndex(feed => feed.url === old_url);
    if (feedIndex === -1) {
      throw new Error(`Feed with URL ${old_url} not found`);
    }
    
    // Update the feed
    this.mockData.feeds[feedIndex] = {
      ...this.mockData.feeds[feedIndex],
      url: new_url,
      site_name: new_site_name
    };
    
    return {
      updateFeed: this.mockData.feeds[feedIndex]
    };
  }
}

// Export mock client for local development
export const mockGraphQLClient = new MockGraphQLClient();
